import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Match, Run, GameState, CLICK_LIMITS } from '@/types/game';
import { fetchArticle, WikiArticle } from '@/lib/wikipedia';
import { useAuth } from '@/hooks/useAuth';

interface UseGameReturn {
  gameState: GameState;
  currentArticle: WikiArticle | null;
  isLoading: boolean;
  error: string | null;
  elapsedTime: number;
  startGame: (match: Match) => Promise<void>;
  navigateToArticle: (title: string) => Promise<void>;
  giveUp: () => void;
  restartGame: () => Promise<void>;
  saveRun: () => Promise<Run | null>;
}

export function useGame(): UseGameReturn {
  const { user } = useAuth();
  const [gameState, setGameState] = useState<GameState>({
    match: null,
    currentArticle: '',
    path: [],
    clicks: 0,
    startTime: null,
    isPlaying: false,
    isCompleted: false,
    isFailed: false,
  });
  const [currentArticle, setCurrentArticle] = useState<WikiArticle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const runIdRef = useRef<string | null>(null);

  // Track paused time for loading
  const pausedTimeRef = useRef<number>(0);
  const loadingStartRef = useRef<number | null>(null);

  // Pause timer during loading
  useEffect(() => {
    if (isLoading && gameState.isPlaying && !gameState.isCompleted && !gameState.isFailed) {
      // Started loading - record when
      loadingStartRef.current = Date.now();
    } else if (!isLoading && loadingStartRef.current) {
      // Finished loading - add paused duration
      pausedTimeRef.current += Date.now() - loadingStartRef.current;
      loadingStartRef.current = null;
    }
  }, [isLoading, gameState.isPlaying, gameState.isCompleted, gameState.isFailed]);

  // Timer effect - pauses during loading
  useEffect(() => {
    if (gameState.isPlaying && gameState.startTime && !gameState.isCompleted && !gameState.isFailed && !isLoading) {
      timerRef.current = setInterval(() => {
        const rawElapsed = Date.now() - gameState.startTime!;
        setElapsedTime(rawElapsed - pausedTimeRef.current);
      }, 100);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameState.isPlaying, gameState.startTime, gameState.isCompleted, gameState.isFailed, isLoading]);

  const startGame = useCallback(async (match: Match) => {
    setIsLoading(true);
    setError(null);
    runIdRef.current = null;
    pausedTimeRef.current = 0;
    loadingStartRef.current = null;

    try {
      // Fetch the starting article
      const article = await fetchArticle(match.start_title);
      
      setGameState({
        match,
        currentArticle: article.title,
        path: [article.title],
        clicks: 0,
        startTime: Date.now(),
        isPlaying: true,
        isCompleted: false,
        isFailed: false,
      });
      setCurrentArticle(article);
      setElapsedTime(0);
    } catch (err) {
      setError('Failed to load starting article. Please try again.');
      console.error('Start game error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const navigateToArticle = useCallback(async (title: string) => {
    if (!gameState.isPlaying || gameState.isCompleted || gameState.isFailed) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const article = await fetchArticle(title);
      const newClicks = gameState.clicks + 1;
      const newPath = [...gameState.path, article.title];
      
      // Check if target reached
      const targetReached = article.title.toLowerCase() === gameState.match?.target_title.toLowerCase();
      
      // Check for failure in under_clicks mode
      let isFailed = false;
      if (gameState.match?.mode === 'under_clicks') {
        const clickLimit = CLICK_LIMITS[gameState.match.difficulty];
        if (newClicks > clickLimit && !targetReached) {
          isFailed = true;
        }
      }

      setGameState(prev => ({
        ...prev,
        currentArticle: article.title,
        path: newPath,
        clicks: newClicks,
        isCompleted: targetReached,
        isFailed,
      }));
      setCurrentArticle(article);

      if (targetReached) {
        setElapsedTime(Date.now() - gameState.startTime!);
      }
    } catch (err) {
      setError('Failed to load article. Please try again.');
      console.error('Navigate error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [gameState]);

  const giveUp = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      isPlaying: false,
      isFailed: true,
    }));
  }, []);

  const restartGame = useCallback(async () => {
    if (gameState.match) {
      await startGame(gameState.match);
    }
  }, [gameState.match, startGame]);

  const saveRun = useCallback(async (): Promise<Run | null> => {
    if (!gameState.match) return null;

    const runData = {
      user_id: user?.id || null,
      match_id: gameState.match.id,
      started_at: new Date(gameState.startTime!).toISOString(),
      ended_at: new Date().toISOString(),
      duration_ms: elapsedTime,
      clicks: gameState.clicks,
      completed: gameState.isCompleted,
      is_public: false,
      path: gameState.path,
    };

    if (!user?.id) {
      // For guests, just return the run data without saving
      return {
        ...runData,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      } as Run;
    }

    try {
      const { data, error } = await supabase
        .from('runs')
        .insert(runData)
        .select()
        .single();

      if (error) throw error;
      
      runIdRef.current = data.id;
      return data as Run;
    } catch (err) {
      console.error('Error saving run:', err);
      return null;
    }
  }, [gameState, elapsedTime, user]);

  return {
    gameState,
    currentArticle,
    isLoading,
    error,
    elapsedTime,
    startGame,
    navigateToArticle,
    giveUp,
    restartGame,
    saveRun,
  };
}
